import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { publicDataFetch, TABLES } from '../../api';
import SEO from '../../components/SEO';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiCalendar, FiUser, FiClock, FiChevronLeft, FiShare2, FiArrowRight } = FiIcons;

function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    // First, check if there's a redirect for this slug
    const redirectRes = await publicDataFetch(`read/${TABLES.REDIRECTS}?old_slug=${slug}`);
    if (redirectRes?.status === 'success' && redirectRes.data?.length > 0) {
      navigate(`/blog/${redirectRes.data[0].new_slug}`, { replace: true });
      return;
    }

    const res = await publicDataFetch(`read/${TABLES.POSTS}?slug=${slug}&status=published`);
    if (res?.status === 'success' && res.data?.length > 0) {
      setPost(res.data[0]);
    } else {
      setError('Artiklen blev ikke fundet.');
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-40">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-accent"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h1 className="text-3xl font-bold text-white mb-4">Ups!</h1>
        <p className="text-brand-text-muted mb-8">{error}</p>
        <Link to="/blog" className="text-brand-accent font-semibold flex items-center justify-center gap-2">
          <SafeIcon icon={FiChevronLeft} /> Tilbage til bloggen
        </Link>
      </div>
    );
  }

  const faqItems = post.faq_data ? JSON.parse(post.faq_data) : [];
  const takeaways = post.takeaways ? JSON.parse(post.takeaways) : [];

  return (
    <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEO 
        title={post.seo_title || post.title}
        description={post.seo_description || post.excerpt}
        image={post.featured_image}
        canonical={post.canonical_url}
        type="article"
      />
      
      <Link to="/blog" className="inline-flex items-center text-brand-text-muted hover:text-brand-accent mb-8 transition-colors">
        <SafeIcon icon={FiChevronLeft} className="mr-2" /> Tilbage til bloggen
      </Link>
      
      <header className="mb-12">
        <div className="flex flex-wrap items-center gap-6 text-sm text-brand-text-muted mb-6">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiCalendar} className="text-brand-accent" />
            {format(new Date(post.published_at), 'd. MMMM yyyy', { locale: da })}
          </div>
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiUser} className="text-brand-accent" />
            {post.author_name || 'Kasper Wood'}
          </div>
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiClock} className="text-brand-accent" />
            {Math.ceil((post.content?.split(' ').length || 0) / 200)} min læsning
          </div>
        </div>
        
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-8 leading-tight">
          {post.title}
        </h1>
        
        {post.excerpt && (
          <p className="text-xl text-brand-text-muted leading-relaxed mb-8 italic border-l-4 border-brand-accent pl-6">
            {post.excerpt}
          </p>
        )}
        
        {post.featured_image && (
          <div className="rounded-3xl overflow-hidden shadow-2xl mb-12">
            <img 
              src={post.featured_image} 
              alt={post.title}
              className="w-full h-auto object-cover max-h-[500px]"
            />
          </div>
        )}
      </header>

      {/* TL;DR Section */}
      {post.tldr_summary && (
        <div className="bg-brand-accent/10 border border-brand-accent/20 rounded-2xl p-8 mb-12">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <span className="text-brand-accent">TL;DR</span> - Hurtigt overblik
          </h2>
          <p className="text-brand-text-muted leading-relaxed">
            {post.tldr_summary}
          </p>
        </div>
      )}

      {/* Main Content */}
      <div 
        className="prose prose-invert prose-brand max-w-none mb-16 text-brand-text-muted leading-loose
          prose-headings:text-white prose-headings:font-bold prose-headings:mt-12 prose-headings:mb-6
          prose-h2:text-3xl prose-h3:text-2xl
          prose-p:mb-6
          prose-a:text-brand-accent prose-a:no-underline hover:prose-a:underline
          prose-strong:text-white
          prose-ul:list-disc prose-ul:pl-6 prose-ul:mb-6
          prose-ol:list-decimal prose-ol:pl-6 prose-ol:mb-6
          prose-blockquote:border-l-brand-accent prose-blockquote:bg-brand-light/5 prose-blockquote:py-2 prose-blockquote:px-6 prose-blockquote:rounded-r-lg"
        dangerouslySetInnerHTML={{ __html: post.content }}
      />

      {/* Takeaways Section */}
      {takeaways.length > 0 && (
        <div className="bg-brand-light/5 border border-brand-light/10 rounded-2xl p-8 mb-12">
          <h2 className="text-2xl font-bold text-white mb-6">Nøgle-takeaways</h2>
          <ul className="space-y-4">
            {takeaways.map((item, index) => (
              <li key={index} className="flex gap-4">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-accent/20 text-brand-accent flex items-center justify-center font-bold text-sm">
                  {index + 1}
                </span>
                <span className="text-brand-text-muted">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* FAQ Section */}
      {faqItems.length > 0 && (
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">Ofte stillede spørgsmål</h2>
          <div className="space-y-6">
            {faqItems.map((item, index) => (
              <div key={index} className="bg-brand-light/5 border border-brand-light/10 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-3">{item.question}</h3>
                <p className="text-brand-text-muted">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <footer className="pt-12 border-t border-brand-light/10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full overflow-hidden bg-brand-light/20 flex-shrink-0">
              <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=100" alt="Author" />
            </div>
            <div>
              <p className="text-xs text-brand-accent font-bold uppercase tracking-wider mb-1">Skrevet af</p>
              <p className="text-lg font-bold text-white">{post.author_name || 'Kasper Wood'}</p>
              <p className="text-sm text-brand-text-muted">Digital Strateg & SEO Ekspert</p>
            </div>
          </div>
          
          <button 
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              alert('Link kopieret!');
            }}
            className="flex items-center gap-2 bg-brand-light/10 hover:bg-brand-light/20 text-white px-6 py-3 rounded-full transition-colors"
          >
            <SafeIcon icon={FiShare2} /> Del artikel
          </button>
        </div>
      </footer>
    </article>
  );
}

export default BlogPost;